import { ServiceLine } from '@/types/service';

export interface ServiceLineChanges {
  toCreate: ServiceLine[];
  toUpdate: ServiceLine[];
  toDelete: ServiceLine[];
}

export interface ServiceLineComparison {
  original: ServiceLine;
  current: ServiceLine;
  hasChanges: boolean;
}

/**
 * Detects changes in service lines between original and current state
 * @param originalServiceLines - The original service lines from the service
 * @param currentServiceLines - The current service lines from the form
 * @returns ServiceLineChanges object with categorized changes
 */
export function detectServiceLineChanges(
  originalServiceLines: ServiceLine[] = [],
  currentServiceLines: ServiceLine[] = []
): ServiceLineChanges {
  const toCreate: ServiceLine[] = [];
  const toUpdate: ServiceLine[] = [];
  const toDelete: ServiceLine[] = [];

  // Create a map of original service lines by ID for quick lookup
  const originalMap = new Map<string, ServiceLine>();
  originalServiceLines.forEach(line => {
    originalMap.set(line.id, line);
  });

  // Create a map of current service lines by ID for quick lookup
  const currentMap = new Map<string, ServiceLine>();
  currentServiceLines.forEach(line => {
    currentMap.set(line.id, line);
  });

  // Check current service lines for new ones and updates
  currentServiceLines.forEach(currentLine => {
    if (isNewServiceLine(currentLine.id)) {
      // This is a new service line (has temp ID or no real ID)
      toCreate.push(currentLine);
    } else {
      // This is an existing service line, check if it has changes
      const originalLine = originalMap.get(currentLine.id);
      if (originalLine) {
        if (hasServiceLineChanged(originalLine, currentLine)) {
          toUpdate.push(currentLine);
        }
      } else {
        // This shouldn't happen, but treat as new if original not found
        toCreate.push(currentLine);
      }
    }
  });

  // Check for deleted service lines (in original but not in current)
  originalServiceLines.forEach(originalLine => {
    if (!currentMap.has(originalLine.id)) {
      toDelete.push(originalLine);
    }
  });

  return {
    toCreate,
    toUpdate,
    toDelete,
  };
}

/**
 * Checks if a service line ID indicates a new service line
 * @param id - The service line ID to check
 * @returns true if this is a new service line
 */
export function isNewServiceLine(id: string): boolean {
  // New service lines have temp IDs that start with "temp-"
  return id.startsWith('temp-') || id === '';
}

/**
 * Compares two service lines to detect changes
 * @param original - The original service line
 * @param current - The current service line
 * @returns true if the service line has changes
 */
export function hasServiceLineChanged(original: ServiceLine, current: ServiceLine): boolean {
  return (
    original.materialId !== current.materialId ||
    original.quantity !== current.quantity ||
    original.loseRate !== current.loseRate
  );
}

/**
 * Compares service lines and returns detailed comparison information
 * @param original - The original service line
 * @param current - The current service line
 * @returns ServiceLineComparison object with detailed information
 */
export function compareServiceLines(original: ServiceLine, current: ServiceLine): ServiceLineComparison {
  return {
    original,
    current,
    hasChanges: hasServiceLineChanged(original, current),
  };
}

/**
 * Validates service lines before processing changes
 * @param serviceLines - The service lines to validate
 * @returns Array of validation errors (empty if valid)
 */
export function validateServiceLines(serviceLines: ServiceLine[]): string[] {
  const errors: string[] = [];

  serviceLines.forEach((line, index) => {
    if (!line.materialId) {
      errors.push(`Service line ${index + 1}: Material is required`);
    }
    if (line.quantity <= 0) {
      errors.push(`Service line ${index + 1}: Quantity must be greater than 0`);
    }
    if (line.loseRate < 0 || line.loseRate > 100 || !Number.isInteger(line.loseRate)) {
      errors.push(`Service line ${index + 1}: Lose rate must be an integer between 0 and 100`);
    }
  });

  // Check for duplicate materials
  const materialIds = serviceLines.map(line => line.materialId).filter(Boolean);
  const duplicates = materialIds.filter((id, index) => materialIds.indexOf(id) !== index);
  if (duplicates.length > 0) {
    errors.push(`Duplicate materials found: ${duplicates.join(', ')}`);
  }

  return errors;
}

/**
 * Filters service lines to only include valid ones (with material selected)
 * @param serviceLines - The service lines to filter
 * @returns Array of valid service lines
 */
export function filterValidServiceLines(serviceLines: ServiceLine[]): ServiceLine[] {
  return serviceLines.filter(line => line.materialId && line.materialId.trim() !== '');
}

/**
 * Prepares service line data for API calls by removing frontend-only fields
 * @param serviceLine - The service line to prepare
 * @returns Service line data ready for API
 */
export function prepareServiceLineForApi(serviceLine: ServiceLine) {
  return {
    materialId: serviceLine.materialId,
    quantity: serviceLine.quantity,
    loseRate: serviceLine.loseRate, // Send lose rate as integer (0-100)
  };
}
